import { FC, MouseEvent, ReactNode, useMemo, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  ChevronLeft,
  ChevronRight,
  CommitOutlined,
  FilterAltOutlined,
  Remove,
  RotateLeft,
  RotateRight,
} from "@mui/icons-material";
import { Box, Button, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";

import { useAnalysisTools } from "../../../../../../context/analysis-tools-context";
import { rotationDirectionIconSx } from "../../../scheme/components/analytics/components/multiline-chart-orbit/styled";
import { useToolbarScroll } from "../../hooks/use-toolbar-scroll";
import {
  MULTI_ANALYSIS_MEASURE_TYPE,
  ROTATION_DIRECTION,
  SIGNAL_DISPLAY_UNIT,
  SIGNAL_TIME_SCALE,
  SPECTRUM_FREQUENCY_SCALE,
  DEFAULT_CELL_DISPLAY_CASCADE_3D,
  type TChartLayoutMode,
  type TSignalDisplayUnit,
} from "../../types";
import {
  FaultFrequenciesIcon,
  FrequencyProfileIcon,
  HarmonicCursorsIcon,
  MarkerIcon,
  SubHarmonicCursorsIcon,
  SyncCursorsIcon,
} from "./toolbar-icons";

interface IToolbarToggleButtonProps {
  titleId: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}

const ToolbarToggleButton: FC<IToolbarToggleButtonProps> = ({
  titleId,
  selected,
  onClick,
  disabled = false,
  children,
}) => (
  <Tooltip title={<FormattedMessage id={titleId} />} arrow disableInteractive>
    <span>
      <ToggleButton
        value={titleId}
        selected={selected}
        onClick={onClick}
        disabled={disabled}
        size="small"
        sx={{
          flexShrink: 0,
          width: 34,
          height: 34,
          p: 0.5,
          border: "1px solid #d8ddea",
          "&.Mui-selected": {
            bgcolor: "rgba(63, 109, 181, 0.12)",
            borderColor: "#3f6db5",
            color: "#3f6db5",
          },
        }}
      >
        {children}
      </ToggleButton>
    </span>
  </Tooltip>
);

const ScrollArrowButton: FC<{ direction: "left" | "right"; disabled: boolean; onClick: () => void }> = ({
  direction,
  disabled,
  onClick,
}) => (
  <IconButton
    size="small"
    disabled={disabled}
    onClick={onClick}
    aria-label={direction === "left" ? "scroll left" : "scroll right"}
    sx={{
      flexShrink: 0,
      width: 28,
      height: 34,
      color: disabled ? "#b8bfd0" : "#4f5d75",
    }}
  >
    {direction === "left" ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
  </IconButton>
);

const EccentricityIcon: FC = () => <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}>ER</Typography>;

interface IMultiAnalysisToolbarProps {
  onFaultFrequenciesToggle?: () => void;
  isFaultFrequenciesLoading?: boolean;
  isRotationDirectionLocked?: boolean;
  canConvertSignalUnits?: boolean;
}

const SIGNAL_UNIT_OPTIONS: readonly { value: TSignalDisplayUnit; label: string; titleId: string }[] = [
  {
    value: SIGNAL_DISPLAY_UNIT.ACCELERATION,
    label: "A",
    titleId: "pages.multiAnalysis.toolbar.signalUnitsAcceleration",
  },
  { value: SIGNAL_DISPLAY_UNIT.VELOCITY, label: "V", titleId: "pages.multiAnalysis.toolbar.signalUnitsVelocity" },
  {
    value: SIGNAL_DISPLAY_UNIT.DISPLACEMENT,
    label: "S",
    titleId: "pages.multiAnalysis.toolbar.signalUnitsDisplacement",
  },
];

const segmentedToggleGroupSx = {
  flexShrink: 0,
  "& .MuiToggleButton-root": {
    width: 34,
    height: 34,
    p: 0,
    fontSize: 11,
    fontWeight: 700,
    border: "1px solid #d8ddea",
    "&.Mui-selected": {
      bgcolor: "rgba(63, 109, 181, 0.12)",
      borderColor: "#3f6db5",
      color: "#3f6db5",
    },
  },
} as const;

const SignalUnitsToggle: FC<{
  value: TSignalDisplayUnit;
  disabled?: boolean;
  onChange: (value: TSignalDisplayUnit) => void;
}> = ({ value, disabled = false, onChange }) => {
  const { formatMessage } = useIntl();

  const handleChange = (_: MouseEvent<HTMLElement>, nextValue: string | null) => {
    if (nextValue === null) {
      return;
    }

    onChange(Number(nextValue) as TSignalDisplayUnit);
  };

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={String(value)}
      onChange={handleChange}
      disabled={disabled}
      aria-label={formatMessage({ id: "pages.multiAnalysis.toolbar.signalUnits" })}
      sx={segmentedToggleGroupSx}
    >
      {SIGNAL_UNIT_OPTIONS.map(option => (
        <ToggleButton
          key={option.value}
          value={String(option.value)}
          aria-label={formatMessage({ id: option.titleId })}
        >
          <Tooltip title={formatMessage({ id: option.titleId })} arrow disableInteractive>
            <Box component="span">{option.label}</Box>
          </Tooltip>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

const SignalTimeScaleButton: FC<{
  revolutions: boolean;
  onClick: () => void;
}> = ({ revolutions, onClick }) => {
  const { formatMessage } = useIntl();
  const title = formatMessage({ id: "pages.multiAnalysis.toolbar.signalTimeScale" });

  return (
    <Tooltip title={title} arrow disableInteractive>
      <span>
        <Button
          size="small"
          variant="outlined"
          aria-label={title}
          onClick={onClick}
          sx={{
            flexShrink: 0,
            minWidth: 48,
            height: 34,
            p: 0.5,
            fontSize: 11,
            fontWeight: 700,
            borderColor: "#d8ddea",
            color: "#3f6db5",
          }}
        >
          {revolutions ? "RPM" : "T"}
        </Button>
      </span>
    </Tooltip>
  );
};

const SpectrumFrequencyScaleButton: FC<{
  orders: boolean;
  onClick: () => void;
}> = ({ orders, onClick }) => {
  const { formatMessage } = useIntl();
  const title = formatMessage({ id: "pages.multiAnalysis.toolbar.spectrumFrequencyScale" });

  return (
    <Tooltip title={title} arrow disableInteractive>
      <span>
        <Button
          size="small"
          variant="outlined"
          aria-label={title}
          onClick={onClick}
          sx={{
            flexShrink: 0,
            minWidth: 48,
            height: 34,
            p: 0.5,
            fontSize: 11,
            fontWeight: 700,
            borderColor: "#d8ddea",
            color: "#3f6db5",
          }}
        >
          {orders ? "Or" : "Hz"}
        </Button>
      </span>
    </Tooltip>
  );
};

export const MultiAnalysisToolbar: FC<IMultiAnalysisToolbarProps> = ({
  onFaultFrequenciesToggle,
  isFaultFrequenciesLoading = false,
  isRotationDirectionLocked = false,
  canConvertSignalUnits = true,
}) => {
  const { formatMessage } = useIntl();
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    activeMeasureType,
    activeMeasureWindow,
    toolbarState,
    setToolbarState,
    getCellDisplayCascade3D,
    setCellDisplayCascade3D,
  } = useAnalysisTools();

  const toolsKey = activeMeasureType ?? "none";
  const { canScrollLeft, canScrollRight, scrollLeft, scrollRight } = useToolbarScroll(scrollRef, toolsKey);

  const activeCellDisplayCascade3D =
    activeMeasureWindow?.templateId != null
      ? getCellDisplayCascade3D(activeMeasureWindow.templateId, activeMeasureWindow.cellId)
      : DEFAULT_CELL_DISPLAY_CASCADE_3D;

  const handleCascadeChange = (_: MouseEvent<HTMLElement>, value: TChartLayoutMode | null) => {
    if (value !== null && activeMeasureWindow?.templateId != null) {
      setCellDisplayCascade3D(activeMeasureWindow.templateId, activeMeasureWindow.cellId, value === "3d");
    }
  };

  const handleSignalUnitsChange = (value: TSignalDisplayUnit) => {
    setToolbarState({ signalDisplayUnits: value });
  };

  const tools = useMemo(() => {
    if (!activeMeasureType || activeMeasureType === MULTI_ANALYSIS_MEASURE_TYPE.POLAR_TREND) {
      return null;
    }

    const signalUnitsToggle = (
      <SignalUnitsToggle
        value={toolbarState.signalDisplayUnits}
        disabled={!canConvertSignalUnits}
        onChange={handleSignalUnitsChange}
      />
    );

    const layoutToggle = (
      <Tooltip
        title={formatMessage({
          id:
            activeMeasureType === MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM
              ? "pages.scheme.analytics.signalLayoutTooltip"
              : "pages.scheme.analytics.spectrumLayoutTooltip",
        })}
        arrow
        disableInteractive
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={activeCellDisplayCascade3D ? "3d" : "2d"}
          onChange={handleCascadeChange}
          sx={segmentedToggleGroupSx}
        >
          <ToggleButton value="2d">{formatMessage({ id: "pages.scheme.analytics.spectrumLayout2d" })}</ToggleButton>
          <ToggleButton value="3d">{formatMessage({ id: "pages.scheme.analytics.spectrumLayout3d" })}</ToggleButton>
        </ToggleButtonGroup>
      </Tooltip>
    );

    const markerToggle = (
      <ToolbarToggleButton
        titleId="pages.multiAnalysis.toolbar.marker"
        selected={toolbarState.markerMode}
        onClick={() => {
          const nextMarkerMode = !toolbarState.markerMode;

          setToolbarState({
            markerMode: nextMarkerMode,
            ...(nextMarkerMode
              ? {}
              : {
                  syncSpectrumCursor: toolbarState.syncSpectrumCursor
                    ? { ...toolbarState.syncSpectrumCursor, markerPhysicalX: null }
                    : null,
                  syncSignalCursor: toolbarState.syncSignalCursor
                    ? { ...toolbarState.syncSignalCursor, markerPhysicalX: null }
                    : null,
                }),
          });
        }}
      >
        <MarkerIcon />
      </ToolbarToggleButton>
    );

    const syncCursorsToggle = (
      <ToolbarToggleButton
        titleId="pages.multiAnalysis.toolbar.syncCursors"
        selected={toolbarState.syncCursors}
        onClick={() => {
          const nextSyncCursors = !toolbarState.syncCursors;

          setToolbarState({
            syncCursors: nextSyncCursors,
            ...(nextSyncCursors ? {} : { syncSpectrumCursor: null, syncSignalCursor: null }),
          });
        }}
      >
        <SyncCursorsIcon />
      </ToolbarToggleButton>
    );

    switch (activeMeasureType) {
      case MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM:
        return (
          <>
            <SpectrumFrequencyScaleButton
              orders={toolbarState.spectrumFrequencyScale === SPECTRUM_FREQUENCY_SCALE.ORDERS}
              onClick={() =>
                setToolbarState({
                  spectrumFrequencyScale:
                    toolbarState.spectrumFrequencyScale === SPECTRUM_FREQUENCY_SCALE.ORDERS
                      ? SPECTRUM_FREQUENCY_SCALE.HERTZ
                      : SPECTRUM_FREQUENCY_SCALE.ORDERS,
                })
              }
            />
            {signalUnitsToggle}
            {markerToggle}
            {syncCursorsToggle}
            <ToolbarToggleButton
              titleId="pages.multiAnalysis.toolbar.frequencyProfile"
              selected={toolbarState.frequencyProfile}
              onClick={() => {
                const nextFrequencyProfile = !toolbarState.frequencyProfile;

                setToolbarState({
                  frequencyProfile: nextFrequencyProfile,
                  ...(nextFrequencyProfile ? {} : { frequencyProfileSelection: null }),
                });
              }}
            >
              <FrequencyProfileIcon />
            </ToolbarToggleButton>
            <ToolbarToggleButton
              titleId="pages.multiAnalysis.toolbar.harmonic"
              selected={toolbarState.harmonicCursors}
              onClick={() => setToolbarState({ harmonicCursors: !toolbarState.harmonicCursors })}
            >
              <HarmonicCursorsIcon />
            </ToolbarToggleButton>
            <ToolbarToggleButton
              titleId="pages.multiAnalysis.toolbar.subharmonic"
              selected={toolbarState.subHarmonicCursors}
              onClick={() => setToolbarState({ subHarmonicCursors: !toolbarState.subHarmonicCursors })}
            >
              <SubHarmonicCursorsIcon />
            </ToolbarToggleButton>
            <ToolbarToggleButton
              titleId="pages.multiAnalysis.toolbar.faults"
              selected={toolbarState.faultFrequencies}
              disabled={isFaultFrequenciesLoading}
              onClick={() => {
                if (onFaultFrequenciesToggle) {
                  void onFaultFrequenciesToggle();
                  return;
                }

                setToolbarState({ faultFrequencies: !toolbarState.faultFrequencies });
              }}
            >
              <FaultFrequenciesIcon />
            </ToolbarToggleButton>
            {layoutToggle}
          </>
        );

      case MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM:
        return (
          <>
            <SignalTimeScaleButton
              revolutions={toolbarState.signalTimeScale === SIGNAL_TIME_SCALE.REVOLUTIONS}
              onClick={() =>
                setToolbarState({
                  signalTimeScale:
                    toolbarState.signalTimeScale === SIGNAL_TIME_SCALE.REVOLUTIONS
                      ? SIGNAL_TIME_SCALE.TIME
                      : SIGNAL_TIME_SCALE.REVOLUTIONS,
                })
              }
            />
            {signalUnitsToggle}
            {markerToggle}
            {syncCursorsToggle}
            <ToolbarToggleButton
              titleId="pages.multiAnalysis.toolbar.orbitPoints"
              selected={toolbarState.showOrbitPoints}
              onClick={() => setToolbarState({ showOrbitPoints: !toolbarState.showOrbitPoints })}
            >
              <CommitOutlined fontSize="small" />
            </ToolbarToggleButton>
            <ToolbarToggleButton
              titleId="pages.multiAnalysis.toolbar.orbitFilter"
              selected={
                toolbarState.orbitFilterOpen ||
                toolbarState.signalAppliedHarmonics.length > 0 ||
                toolbarState.signalAppliedBandpassHz !== null
              }
              onClick={() => setToolbarState({ orbitFilterOpen: !toolbarState.orbitFilterOpen })}
            >
              <FilterAltOutlined fontSize="small" />
            </ToolbarToggleButton>
            {layoutToggle}
          </>
        );

      case MULTI_ANALYSIS_MEASURE_TYPE.ORBIT:
        return (
          <>
            <Box
              aria-label={formatMessage({ id: "pages.multiAnalysis.toolbar.rotation" })}
              sx={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <ToolbarToggleButton
                titleId="enums.rotationDirection.counterClockwise"
                selected={toolbarState.rotationDirection === ROTATION_DIRECTION.COUNTER_CLOCKWISE}
                onClick={() => setToolbarState({ rotationDirection: ROTATION_DIRECTION.COUNTER_CLOCKWISE })}
                disabled={isRotationDirectionLocked}
              >
                <RotateLeft sx={rotationDirectionIconSx} />
              </ToolbarToggleButton>
              <ToolbarToggleButton
                titleId="enums.rotationDirection.undefined"
                selected={toolbarState.rotationDirection === ROTATION_DIRECTION.UNDEFINED}
                onClick={() => setToolbarState({ rotationDirection: ROTATION_DIRECTION.UNDEFINED })}
                disabled={isRotationDirectionLocked}
              >
                <Remove sx={rotationDirectionIconSx} />
              </ToolbarToggleButton>
              <ToolbarToggleButton
                titleId="enums.rotationDirection.clockwise"
                selected={toolbarState.rotationDirection === ROTATION_DIRECTION.CLOCKWISE}
                onClick={() => setToolbarState({ rotationDirection: ROTATION_DIRECTION.CLOCKWISE })}
                disabled={isRotationDirectionLocked}
              >
                <RotateRight sx={rotationDirectionIconSx} />
              </ToolbarToggleButton>
            </Box>
            <ToolbarToggleButton
              titleId="pages.multiAnalysis.toolbar.orbitPoints"
              selected={toolbarState.showOrbitPoints}
              onClick={() => setToolbarState({ showOrbitPoints: !toolbarState.showOrbitPoints })}
            >
              <CommitOutlined fontSize="small" />
            </ToolbarToggleButton>
            <ToolbarToggleButton
              titleId="pages.multiAnalysis.toolbar.eccentricity"
              selected={toolbarState.eccentricity}
              onClick={() => {
                const nextEccentricity = !toolbarState.eccentricity;

                setToolbarState({
                  eccentricity: nextEccentricity,
                  ...(nextEccentricity ? {} : { orbitRadialClearance: null }),
                });
              }}
            >
              <EccentricityIcon />
            </ToolbarToggleButton>
            <ToolbarToggleButton
              titleId="pages.multiAnalysis.toolbar.orbitFilter"
              selected={
                toolbarState.orbitFilterOpen ||
                toolbarState.orbitAppliedBins.length > 0 ||
                toolbarState.orbitAppliedBandpassHz !== null
              }
              onClick={() => setToolbarState({ orbitFilterOpen: !toolbarState.orbitFilterOpen })}
            >
              <FilterAltOutlined fontSize="small" />
            </ToolbarToggleButton>
          </>
        );

      default:
        return null;
    }
  }, [
    activeCellDisplayCascade3D,
    activeMeasureType,
    canConvertSignalUnits,
    formatMessage,
    isFaultFrequenciesLoading,
    isRotationDirectionLocked,
    onFaultFrequenciesToggle,
    setToolbarState,
    toolbarState,
  ]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, px: 0.25, py: 0.5 }}>
      <ScrollArrowButton direction="left" disabled={!canScrollLeft} onClick={scrollLeft} />
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          px: 0.5,
        }}
      >
        {tools ?? (
          <Typography sx={{ fontSize: 12, color: "#8b93a7", px: 0.5, whiteSpace: "nowrap" }}>
            <FormattedMessage id="pages.multiAnalysis.toolbar.noTools" />
          </Typography>
        )}
      </Box>
      <ScrollArrowButton direction="right" disabled={!canScrollRight} onClick={scrollRight} />
    </Box>
  );
};
